import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';
import { ArrowRight, Target, Users, Sparkles } from 'lucide-react';

const values = [
  {
    title: 'Context over pattern.',
    desc: 'The industry has spent a decade teaching software to recognize patterns. We teach it to understand your firm.',
    icon: Sparkles,
  },
  {
    title: 'Firm-driven, not vendor-driven.',
    desc: 'Every feature we ship is shaped by the controllers, partners, and reviewers who use SnuggleMind every day.',
    icon: Users,
  },
  {
    title: 'Relentless about the last mile.',
    desc: 'Generic AI gets you 80% of the way. We obsess over the 20% that separates usable from magical.',
    icon: Target,
  },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen" style={{ background: '#F7F6F2' }} data-testid="about-page">
      <PublicHeader />

      {/* HERO */}
      <section className="pt-20 pb-16 lg:pt-28 lg:pb-20">
        <div className="max-w-[1280px] mx-auto px-6 max-w-4xl">
          <p className="text-[11px] uppercase tracking-[0.24em] font-semibold mb-5" style={{ color: '#D98A4A' }}>About SnuggleMind</p>
          <h1 className="font-heading font-semibold tracking-[-0.03em] mb-8" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)', color: '#0A3622', lineHeight: 1 }}>
            We're building the bookkeeping platform the profession has been waiting for.
          </h1>
          <p className="text-[19px] leading-relaxed max-w-2xl" style={{ color: '#4B5563' }}>
            SnuggleMind was founded on a simple distinction: <strong style={{ color: '#0A3622' }}>pattern recognition is not business context.</strong> Most AI bookkeeping tools confuse the two. We don't.
          </p>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="py-20" style={{ background: '#FFFFFF', borderTop: '1px solid #E5E3DB', borderBottom: '1px solid #E5E3DB' }}>
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-heading font-semibold tracking-tight mb-8" style={{ fontSize: 'clamp(2rem, 3.5vw, 2.75rem)', color: '#0A3622', lineHeight: 1.1 }}>
            Why we built this.
          </h2>
          <div className="space-y-5 text-[17px] leading-relaxed" style={{ color: '#4B5563' }}>
            <p>
              Accountants and controllers spend an enormous percentage of their month on work that software should have solved by now — categorizing the same vendor for the twentieth time, hunting down a missing receipt, reconciling a statement that doesn't quite tie out.
            </p>
            <p>
              The problem wasn't a lack of AI. It was a lack of <em>context</em>. Every firm has its own chart of accounts, its own policies, its own way of coding a meal or a software subscription. A generic pattern-matcher can't respect that. An LLM alone hallucinates. Rules engines calcify.
            </p>
            <p>
              SnuggleMind is a <strong style={{ color: '#0A3622' }}>hybrid</strong> — GPT-5.2 reasoning, a policy-driven rules engine, a per-client context store, and a learning loop that gets smarter every time you correct it. It's the first bookkeeping brain that actually respects how your firm works.
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24">
        <div className="max-w-[1280px] mx-auto px-6">
          <h3 className="font-heading font-semibold tracking-tight mb-14 text-center" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: '#0A3622' }}>
            What we stand for.
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((v) => (
              <div key={v.title} className="p-8 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid #E5E3DB' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ background: 'rgba(217,138,74,0.12)' }}>
                  <v.icon size={24} style={{ color: '#D98A4A' }} />
                </div>
                <h4 className="font-heading text-[20px] font-semibold mb-3" style={{ color: '#0A3622' }}>{v.title}</h4>
                <p className="text-[14.5px] leading-relaxed" style={{ color: '#4B5563' }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" style={{ background: '#0A3622' }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-semibold tracking-tight text-white mb-6" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1 }}>
            Join the firms shaping what's next.
          </h2>
          <Link to="/register" className="inline-flex items-center gap-2 h-12 px-8 rounded-full text-[15px] font-body font-semibold" style={{ background: '#D98A4A', color: '#FFFFFF' }} data-testid="about-cta">
            Start Free Trial <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
